import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Icon } from "@iconify/react";

// ** Components
import Datagrid from "@/components/tables/DataGrid.tsx";
import ApplicationDetailModal from "@/views/admin/applications/ApplicationDetailModal.tsx";
import DeleteModal from "@/components/modal/DeleteModal.tsx";
import PurgeModal from "@/views/admin/applications/PurgeModal.tsx";
import AppCard from "@/components/AppCard.tsx";

// ** Hooks
import {
  useGetApplicationsQuery,
  useLazyExportApplicationsQuery,
} from "@/api/applicationsApi.ts";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

// ** Constants
import { APPLICATION_STATUS_COLORS } from "@/constants";

type Application =
  components["schemas"]["Application.jsonld-application.read_read"];

export const Route = createFileRoute("/admin/applications")({
  component: ApplicationsPage,
});

function ApplicationsPage() {
  const { t } = useTranslation();

  // Modal states
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [studioNumberFilter, setStudioNumberFilter] = useState<string>("");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [recentNotesPeriod, setRecentNotesPeriod] = useState<string>("");

  // Export
  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportApplicationsQuery();

  const handleView = (application: Application) => {
    setSelectedApplication(application);
    setDetailOpen(true);
  };

  const handleDelete = (application: Application) => {
    setSelectedApplication(application);
    setDeleteOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedApplication(null);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedApplication(null);
  };

  const handleExport = async () => {
    const result = await triggerExport();
    if (result.data) {
      const link = document.createElement("a");
      link.href = result.data;
      link.download = `candidatures_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(result.data);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Calculate date for recent notes filter
  const getRecentNotesDate = (period: string): string | null => {
    if (!period) return null;
    const now = new Date();
    const date = new Date(now);
    switch (period) {
      case "7d":
        date.setDate(date.getDate() - 7);
        break;
      case "30d":
        date.setDate(date.getDate() - 30);
        break;
      case "90d":
        date.setDate(date.getDate() - 90);
        break;
      default:
        return null;
    }
    return date.toISOString().split("T")[0];
  };

  const columns: GridColDef<Application>[] = [
    {
      field: "createdAt",
      headerName: t("admin.applications.columns.date"),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: "lastName",
      headerName: t("admin.applications.columns.lastName"),
      width: 150,
    },
    {
      field: "firstName",
      headerName: t("admin.applications.columns.firstName"),
      width: 150,
    },
    {
      field: "email",
      headerName: t("admin.applications.columns.email"),
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: t("admin.applications.columns.phone"),
      width: 130,
    },
    {
      field: "studio",
      headerName: t("admin.applications.columns.studio"),
      width: 120,
      valueGetter: (_value: unknown, row: Application) =>
        row.studio
          ? `Studio ${row.studio.number}`
          : t("admin.applications.general"),
    },
    {
      field: "notesCount",
      headerName: t("admin.applications.notes.title"),
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Application>) => {
        const count = params.row.notesCount ?? 0;
        return count > 0 ? (
          <Chip label={count} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2" color="text.disabled">
            -
          </Typography>
        );
      },
    },
    {
      field: "documentsCount",
      headerName: t("admin.applications.columns.documents"),
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Application>) => {
        const count = params.row.documentsCount ?? 0;
        return count > 0 ? (
          <Chip
            icon={<Icon icon="tabler:file" width={14} />}
            label={count}
            size="small"
            color="info"
            variant="outlined"
          />
        ) : (
          <Typography variant="body2" color="text.disabled">
            -
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: t("admin.applications.columns.status"),
      width: 120,
      renderCell: (params: GridRenderCellParams<Application>) => (
        <Chip
          label={t(`status.${params.row.status}`)}
          color={APPLICATION_STATUS_COLORS[params.row.status ?? "new"] ?? "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: t("admin.common.actions"),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Application>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t("admin.applications.view")}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleView(params.row);
              }}
            >
              <Icon icon="tabler:eye" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("admin.common.delete")}>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <Icon icon="tabler:trash" width={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Build query params with filters
  const queryParams: Record<string, unknown> = {};
  if (statusFilter) queryParams.status = statusFilter;
  if (studioNumberFilter) queryParams["studio.number"] = studioNumberFilter;
  if (searchFilter) queryParams.search = searchFilter;
  const recentNotesDate = getRecentNotesDate(recentNotesPeriod);
  if (recentNotesDate) queryParams["hasRecentNotes[after]"] = recentNotesDate;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.applications.title")}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Icon icon="tabler:trash" />}
            onClick={() => setPurgeOpen(true)}
          >
            {t("admin.applications.purgeRgpd")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Icon icon="tabler:download" />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting
              ? t("admin.common.loading")
              : t("admin.applications.export")}
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          label={t("admin.common.search")}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder={t("admin.applications.filters.searchPlaceholder")}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("admin.applications.filters.status")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("admin.applications.filters.status")}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t("admin.common.all")}</MenuItem>
            <MenuItem value="new">{t("status.new")}</MenuItem>
            <MenuItem value="contacted">{t("status.contacted")}</MenuItem>
            <MenuItem value="accepted">{t("status.accepted")}</MenuItem>
            <MenuItem value="refused">{t("status.refused")}</MenuItem>
            <MenuItem value="archived">{t("status.archived")}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={t("admin.applications.filters.studioNumber")}
          value={studioNumberFilter}
          onChange={(e) => setStudioNumberFilter(e.target.value)}
          placeholder="Ex: 101"
          sx={{ width: 100 }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>
            {t("admin.applications.filters.recentNotes")}
          </InputLabel>
          <Select
            value={recentNotesPeriod}
            label={t("admin.applications.filters.recentNotes")}
            onChange={(e) => setRecentNotesPeriod(e.target.value)}
          >
            <MenuItem value="">{t("admin.common.all")}</MenuItem>
            <MenuItem value="7d">
              {t("admin.applications.filters.last7Days")}
            </MenuItem>
            <MenuItem value="30d">
              {t("admin.applications.filters.last30Days")}
            </MenuItem>
            <MenuItem value="90d">
              {t("admin.applications.filters.last90Days")}
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* DataGrid */}
      <AppCard>
        <Datagrid
          columns={columns}
          queryFunction={useGetApplicationsQuery}
          queryFunctionParam={queryParams}
          defaultOrder="createdAt"
          searchField="search"
        />
      </AppCard>

      {/* Detail Modal */}
      <ApplicationDetailModal
        open={detailOpen}
        onClose={handleDetailClose}
        application={selectedApplication}
      />

      {/* Delete Modal */}
      {selectedApplication && (
        <DeleteModal
          open={deleteOpen}
          onClose={handleDeleteClose}
          elementLabel={selectedApplication.fullName ?? "Candidature"}
          elementIri={`/api/applications/${selectedApplication.uuid}`}
          onSuccess={handleDeleteClose}
        />
      )}

      {/* Purge Modal */}
      <PurgeModal open={purgeOpen} onClose={() => setPurgeOpen(false)} />
    </Box>
  );
}
